import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ApartmentOutlined } from "@ant-design/icons";

import { actions as CommonActions } from "../../store/commonRedux/actions";
import styles from "./styles.module.css";

/**
 * How long the screen stays up at the very least, counted from the click rather
 * than from this document — the switch reloads the page, and measuring from the
 * new document would make the two halves add up to twice the intended wait.
 *
 * A switch on a warm cache is over in about a fifth of a second, which reads as
 * a flicker rather than as a change of workspace.
 */
const MIN_MS = 1600;

/** Length of the fade in `styles.module.css`. */
const FADE_MS = 220;

/** If boot never reports in — a failed chunk, an offline reload — the overlay
 *  must not become a permanent page. */
const MAX_MS = 12000;

/**
 * The screen that covers a workspace switch.
 *
 * Switching organization reloads the whole document: roles, permissions,
 * modules and every cached list belong to one organization, and there is no
 * honest way to swap them in place. So this is raised twice from one payload —
 * over the old panel before the reload (`utils/orgSwitch`), and again as soon
 * as React mounts in the new document, because the payload lives in the
 * persisted `common` slice and survives the reload.
 *
 * Between those two halves there is a gap while the bundle boots. It is not a
 * blank page — `index.css` paints the app's own ground colour — and closing it
 * completely would mean putting markup, styles and a script into `index.html`
 * to paint before React exists. Not worth it for the ~200ms it saves.
 */
const OrgSwitchSplash = () => {
  const pending = useSelector(({ common }) => common.orgSwitchPending);
  /* Blacklisted from persistence, so it is false again on every reload: it says
     "this document has finished loading the organization", which is exactly the
     question a persisted flag could not answer. `BasePage` sets it. */
  const booted = useSelector(({ common }) => common.orgBootDone);
  const dispatch = useDispatch();

  const [leaving, setLeaving] = useState(false);

  const startedAt = pending?.at;

  useEffect(() => {
    if (!pending) {
      setLeaving(false);
      return undefined;
    }

    /* Two conditions, whichever finishes last: the panel underneath is ready,
       and the screen has been up long enough to be read. */
    const elapsed = Date.now() - (startedAt || Date.now());
    const wait = booted ? Math.max(0, MIN_MS - elapsed) : MAX_MS - elapsed;

    const timer = window.setTimeout(() => setLeaving(true), Math.max(0, wait));
    return () => window.clearTimeout(timer);
  }, [pending, booted, startedAt]);

  /* Cleared only after the fade, so the panel underneath is never revealed by a
     cut. */
  useEffect(() => {
    if (!leaving) return undefined;

    const timer = window.setTimeout(
      () => dispatch(CommonActions.setVariables({ orgSwitchPending: null })),
      FADE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [leaving, dispatch]);

  if (!pending) return null;

  const title = pending.title?.trim();

  return (
    <div
      className={`${styles.splash} ${leaving ? styles.leaving : ""}`}
      role="status"
      aria-live="polite"
    >
      <div className={styles.card}>
        {pending.avatar ? (
          <img className={styles.logo} src={pending.avatar} alt="" />
        ) : (
          /* An organization with no logo still needs something the same size,
             or the card jumps the day someone uploads one. */
          <span className={`${styles.logo} ${styles.logoEmpty}`}>
            {title ? title.charAt(0) : <ApartmentOutlined />}
          </span>
        )}

        <span className={styles.spinner} />

        <p className={styles.title}>
          {title ? `در حال ورود به «${title}»` : "در حال تغییر فضای کاری"}
        </p>
        <p className={styles.hint}>
          یه لحظه صبر کن، داریم فضای کاری رو آماده می‌کنیم…
        </p>
      </div>
    </div>
  );
};

export default OrgSwitchSplash;
